import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Play, RotateCcw, FileCode, BarChart3, AlertTriangle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { LoadingSpinner } from '@/components/ui/loading-spinner'
import { multiSymbolApi } from '@/services/api/multi-symbol'
import { jobsApi } from '@/services/api/jobs'
import { DatasetSelector, AnalysisConfig, ResultsVisualization } from '@/components/multi-symbol'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { getDefaultDslForType } from '@/components/multi-symbol/default-dsl-templates'
import type { MultiSymbolDataset, MultiSymbolAnalysisResponse } from '@/types/multi-symbol'
import { showErrorToast, showSuccessToast } from '@/utils/error-handler'

export default function MultiSymbolAnalysisPage() {
  const { t } = useTranslation()
  const [activeTab, setActiveTab] = useState<'datasets' | 'config' | 'results'>('datasets')

  // State
  const [selectedDatasets, setSelectedDatasets] = useState<MultiSymbolDataset[]>([])
  const [analysisType, setAnalysisType] = useState('comparison')
  const [dslCode, setDslCode] = useState(() => getDefaultDslForType('comparison'))

  const handleAnalysisTypeChange = (type: string) => {
    setAnalysisType(type)
    if (type !== 'custom') setDslCode(getDefaultDslForType(type))
  }
  const [analysisOptions, setAnalysisOptions] = useState<Record<string, any>>({})
  const [dateRange, setDateRange] = useState<{ start: string; end: string } | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false) // true only while POST create-job is in flight
  const [isJobPending, setIsJobPending] = useState(false) // true from job created until poll completes/fails
  const [results, setResults] = useState<MultiSymbolAnalysisResponse | null>(null)
  const [resultsViewMode, setResultsViewMode] = useState('overview')

  // Total symbols: sum of symbols from partitioned datasets, or one per separate dataset
  const totalSymbolCount = selectedDatasets.reduce((sum, ds) => {
    if ('symbols' in ds && Array.isArray(ds.symbols)) return sum + ds.symbols.length
    return sum + 1
  }, 0)
  // Flat list of symbols for duplicate check (same logic as backend)
  const effectiveSymbolList = selectedDatasets.flatMap(ds =>
    'symbols' in ds && Array.isArray(ds.symbols) ? ds.symbols.map(s => String(s).trim()) : [String((ds as any).symbol ?? '').trim()].filter(Boolean)
  )
  const hasDuplicateSymbols = effectiveSymbolList.length !== new Set(effectiveSymbolList).size
  const canRunAnalysis = totalSymbolCount >= 2 && dslCode.trim().length > 0 && !hasDuplicateSymbols

  // Run analysis: create job via POST /multi-symbol/analyze, then poll GET /jobs/:id until completed/failed
  const runAnalysis = async () => {
    if (!canRunAnalysis) return

    setIsSubmitting(true)
    setResults(null)
    setActiveTab('results')

    try {
      const request = {
        dslCode: dslCode.trim(),
        datasets: selectedDatasets,
        ...(dateRange?.start && dateRange?.end && { dateRange: { start: dateRange.start, end: dateRange.end } }),
        analysis: {
          type: analysisType as 'comparison' | 'correlation' | 'spread' | 'basket' | 'custom',
          options: analysisOptions,
        },
      }

      const { jobId } = await multiSymbolApi.analyze(request)
      setIsSubmitting(false)
      setIsJobPending(true)
      showSuccessToast(t('multiSymbol.jobCreated'))

      const pollIntervalMs = 1500
      const maxAttempts = 600 // ~15 min
      let attempts = 0

      const poll = async (): Promise<void> => {
        const job = await jobsApi.get(jobId)
        if (job.status === 'completed') {
          if (job.result != null) {
            setResults(job.result as MultiSymbolAnalysisResponse)
            showSuccessToast(t('multiSymbol.analysisCompleted'))
          } else {
            showErrorToast(new Error(t('multiSymbol.noResultReturned')), t('multiSymbol.analysisFailed'))
          }
          setIsJobPending(false)
          return
        }
        if (job.status === 'failed') {
          showErrorToast(new Error(job.error || t('multiSymbol.analysisFailed')), t('multiSymbol.analysisFailed'))
          setIsJobPending(false)
          return
        }
        if (job.status === 'expired') {
          showErrorToast(new Error(t('multiSymbol.jobExpired')), t('multiSymbol.analysisFailed'))
          setIsJobPending(false)
          return
        }
        attempts++
        if (attempts >= maxAttempts) {
          showErrorToast(new Error(t('multiSymbol.analysisTimedOut')), t('multiSymbol.analysisFailed'))
          setIsJobPending(false)
          return
        }
        setTimeout(poll, pollIntervalMs)
      }

      await poll()
    } catch (error) {
      showErrorToast(error, t('multiSymbol.analysisFailed'))
      setIsSubmitting(false)
    }
  }

  // Reset analysis
  const resetAnalysis = () => {
    setSelectedDatasets([])
    setAnalysisType('comparison')
    setDslCode(getDefaultDslForType('comparison'))
    setAnalysisOptions({})
    setDateRange(null)
    setResults(null)
    setActiveTab('datasets')
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">{t('multiSymbol.title')}</h1>
          <p className="text-muted-foreground mt-2">
            {t('multiSymbol.subtitle')}
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={resetAnalysis}
            disabled={isSubmitting || isJobPending}
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            {t('multiSymbol.reset')}
          </Button>
          <Button
            onClick={runAnalysis}
            disabled={!canRunAnalysis || isSubmitting || isJobPending}
            className="min-w-[120px]"
          >
            {isSubmitting ? (
              <>
                <div className="w-4 h-4 mr-2 animate-spin rounded-full border-2 border-current border-t-transparent" />
                {t('multiSymbol.running')}
              </>
            ) : (
              <>
                <Play className="w-4 h-4 mr-2" />
                {t('multiSymbol.runAnalysis')}
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Progress Indicator */}
      <div className="flex items-center space-x-4 mb-6">
        <div className={`flex items-center space-x-2 ${activeTab === 'datasets' ? 'text-primary' : selectedDatasets.length > 0 ? 'text-green-600' : 'text-muted-foreground'}`}>
          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
            selectedDatasets.length > 0 ? 'bg-green-100 text-green-800' : activeTab === 'datasets' ? 'bg-primary text-primary-foreground' : 'bg-muted'
          }`}>
            {selectedDatasets.length > 0 ? '✓' : '1'}
          </div>
          <span className="text-sm font-medium">{t('multiSymbol.stepSelectDatasets')}</span>
        </div>

        <div className="w-8 h-px bg-border" />

        <div className={`flex items-center space-x-2 ${activeTab === 'config' ? 'text-primary' : dslCode.trim() ? 'text-green-600' : 'text-muted-foreground'}`}>
          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
            dslCode.trim() ? 'bg-green-100 text-green-800' : activeTab === 'config' ? 'bg-primary text-primary-foreground' : 'bg-muted'
          }`}>
            {dslCode.trim() ? '✓' : '2'}
          </div>
          <span className="text-sm font-medium">{t('multiSymbol.stepConfigureAnalysis')}</span>
        </div>

        <div className="w-8 h-px bg-border" />

        <div className={`flex items-center space-x-2 ${activeTab === 'results' ? 'text-primary' : results ? 'text-green-600' : isSubmitting ? 'text-primary' : 'text-muted-foreground'}`}>
          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
            results ? 'bg-green-100 text-green-800' : activeTab === 'results' || isSubmitting ? 'bg-primary text-primary-foreground' : 'bg-muted'
          }`}>
            {results ? '✓' : isSubmitting ? <LoadingSpinner size="sm" className="text-primary-foreground" /> : '3'}
          </div>
          <span className="text-sm font-medium">{t('multiSymbol.stepViewResults')}</span>
        </div>
      </div>

      {/* Main Content */}
      <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as any)}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="datasets" disabled={isSubmitting || isJobPending}>
            <FileCode className="w-4 h-4 mr-2" />
            {t('multiSymbol.tabDatasets')}
          </TabsTrigger>
          <TabsTrigger value="config" disabled={selectedDatasets.length === 0 || isSubmitting || isJobPending}>
            <BarChart3 className="w-4 h-4 mr-2" />
            {t('multiSymbol.tabConfiguration')}
          </TabsTrigger>
          <TabsTrigger value="results" disabled={!results && !isSubmitting && !isJobPending}>
            {(isSubmitting || isJobPending) ? <LoadingSpinner size="sm" className="mr-2" /> : <BarChart3 className="w-4 h-4 mr-2" />}
            {t('multiSymbol.tabResults')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="datasets" className="mt-6 space-y-4">
          {selectedDatasets.length > 1 && (
            <Alert className="border-amber-500/60 bg-amber-50/80 dark:bg-amber-950/20 dark:border-amber-600/50">
              <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-500" />
              <AlertTitle className="text-amber-800 dark:text-amber-200">{t('multiSymbol.experimentalWarningTitle')}</AlertTitle>
              <AlertDescription>
                {t('multiSymbol.experimentalWarningDescription')}
              </AlertDescription>
            </Alert>
          )}
          <DatasetSelector
            selectedDatasets={selectedDatasets}
            onDatasetsChange={setSelectedDatasets}
          />
        </TabsContent>

        <TabsContent value="config" className="mt-6 space-y-6">
          {/* Optional date range filter */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">{t('multiSymbol.dateRangeFilter')}</CardTitle>
              <p className="text-sm text-muted-foreground">{t('multiSymbol.dateRangeFilterDescription')}</p>
            </CardHeader>
            <CardContent className="flex flex-wrap items-end gap-4">
              <div className="flex flex-col gap-2">
                <label htmlFor="date-range-start" className="text-sm font-medium">
                  {t('multiSymbol.dateRangeStart')}
                </label>
                <input
                  id="date-range-start"
                  type="date"
                  className="flex h-9 rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors"
                  value={dateRange?.start ?? ''}
                  onChange={(e) => {
                    const start = e.target.value
                    const end = dateRange?.end ?? ''
                    setDateRange(start || end ? { start, end } : null)
                  }}
                />
              </div>
              <div className="flex flex-col gap-2">
                <label htmlFor="date-range-end" className="text-sm font-medium">
                  {t('multiSymbol.dateRangeEnd')}
                </label>
                <input
                  id="date-range-end"
                  type="date"
                  className="flex h-9 rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors"
                  value={dateRange?.end ?? ''}
                  onChange={(e) => {
                    const end = e.target.value
                    const start = dateRange?.start ?? ''
                    setDateRange(start || end ? { start, end } : null)
                  }}
                />
              </div>
              {(dateRange?.start || dateRange?.end) && (
                <Button type="button" variant="ghost" size="sm" onClick={() => setDateRange(null)}>
                  {t('multiSymbol.clearDateRange')}
                </Button>
              )}
            </CardContent>
          </Card>
          <AnalysisConfig
            selectedDatasets={selectedDatasets}
            dslCode={dslCode}
            analysisType={analysisType}
            analysisOptions={analysisOptions}
            onDslCodeChange={setDslCode}
            onAnalysisTypeChange={handleAnalysisTypeChange}
            onAnalysisOptionsChange={setAnalysisOptions}
          />
        </TabsContent>

        <TabsContent value="results" className="mt-6">
          {isSubmitting && !results ? (
            <div className="flex flex-col items-center justify-center min-h-[320px] rounded-lg border border-dashed bg-muted/30">
              <LoadingSpinner size="lg" />
              <p className="mt-4 text-sm font-medium text-foreground">{t('multiSymbol.runningMessage')}</p>
              <p className="mt-1 text-xs text-muted-foreground">{t('multiSymbol.fetchingResults')}</p>
            </div>
          ) : results ? (
            <ResultsVisualization
              results={results}
              viewMode={resultsViewMode as any}
              onViewModeChange={(mode) => setResultsViewMode(mode)}
            />
          ) : (
            <Card>
              <CardContent className="text-center py-12">
                {isJobPending ? (
                  <>
                    <LoadingSpinner size="lg" className="mx-auto mb-4" />
                    <h3 className="text-lg font-medium mb-2">{t('multiSymbol.jobRunning')}</h3>
                    <p className="text-muted-foreground mb-4">
                      {t('multiSymbol.jobRunningDescription')}
                    </p>
                  </>
                ) : (
                  <>
                    <BarChart3 className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                    <h3 className="text-lg font-medium mb-2">{t('multiSymbol.noResultsYet')}</h3>
                    <p className="text-muted-foreground mb-4">
                      {t('multiSymbol.noResultsDescription')}
                    </p>
                    <Button onClick={() => setActiveTab('datasets')} variant="outline">
                      {t('multiSymbol.goToDatasets')}
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  )
}